"use strict";

exports.__esModule = true;
exports.TypeWithinSegment = exports.TimedMetadata = void 0;
var _YoLog = require("../Utils/YoLog");
/*
 * COPYRIGHT 2020-2023 YOSPACE TECHNOLOGIES LTD. ALL RIGHTS RESERVED.
 * The contents of this file are proprietary and confidential.
 * Unauthorised copying of this file, via any medium is strictly prohibited.
 */
/**
 * Encapsulation of a timed metadata payload.
 * @hideconstructor
 */
var TimedMetadata = exports.TimedMetadata = /*#__PURE__*/function () {
  function TimedMetadata(mediaId, segmentNumber, segmentCount, type, offset, playhead) {
    /** @private */
    this.mediaId = TimedMetadata.decodeString(mediaId);
    /** @private */
    this.offset = offset;
    /** @private */
    this.segmentCount = segmentCount;
    /** @private */
    this.segmentNumber = segmentNumber;
    /** @private */
    this.playhead = playhead;
    /** @private */
    this.timestamp = Date.now();
    /** @private */
    this.typeWithinSegment = type;
  }

  /**
   * Factory method to create an instance of TimedMetadata
   * @param {string} ymid the YMID metadata frame ID (media Id)
   * @param {string} yseq the YSEQ metadata frame ID (sequence)
   * @param {string} ytyp the YTYP metadata frame ID (type within segment)
   * @param {number} ydur the YDUR metadata frame ID (offset from beginning of segment/fragment)
   * @param {number} playhead the playhead position at which this metadata is defined (in milliseconds)
   *
   * @return {TimedMetadata} a newly created metadata object
   */
  TimedMetadata.createFromMetadata = function createFromMetadata(ymid, yseq, ytyp, ydur, playhead) {
    if (!ymid) {
      _YoLog.YoLog.e("Failed to interpret ymid");
      return null;
    }
    ytyp = TimedMetadata.decodeString(ytyp);
    var type = TypeWithinSegment.getForKey(ytyp);
    if (type === null || type === undefined) {
      _YoLog.YoLog.e("Failed to interpret ytyp");
      return null;
    }
    var seqElem = TimedMetadata.decodeString(yseq).split(":");
    if (seqElem.length !== 2) {
      _YoLog.YoLog.e("Failed to interpret yseq");
      return null;
    }
    var seqNum = parseInt(seqElem[0]);
    var seqCount = parseInt(seqElem[1]);
    if (isNaN(seqNum) || seqNum === 0 || isNaN(seqCount) || seqCount === 0) {
      _YoLog.YoLog.e("Failed to interpret yseq");
      return null;
    }
    var f = parseFloat(this.decodeString(ydur));
    var offset = Number.isNaN(f) ? 0 : Math.round(f * 1000);
    if (playhead < 0) {
      _YoLog.YoLog.w("Invalid playhead provided:" + playhead);
      return null;
    }
    return new TimedMetadata(ymid, seqNum, seqCount, type, offset, playhead);
  };
  TimedMetadata.decodeString = function decodeString(data) {
    return data && data.length > 0 && data[0] === 0x03 ? data.substring(1) : data;
  }

  /**
   * @return {string} the mediaId of the timed metadata
   */;
  var _proto = TimedMetadata.prototype;
  _proto.getMediaId = function getMediaId() {
    return this.mediaId;
  }

  /**
   * @return {number} the value of the metadata offset in seconds
   */;
  _proto.getOffset = function getOffset() {
    return this.offset;
  }

  /**
   * @return {number} the playhead position of the timed metadata, in milliseconds
   */;
  _proto.getPlayhead = function getPlayhead() {
    return this.playhead;
  }

  /**
   * @return {number} the number of segments making up the advert
   */;
  _proto.getSegmentCount = function getSegmentCount() {
    return this.segmentCount;
  }

  /**
   * @return {number} the segment number to which this timed metadata belongs 
   */;
  _proto.getSegmentNumber = function getSegmentNumber() {
    return this.segmentNumber;
  };
  _proto.getTimestamp = function getTimestamp() {
    return this.timestamp;
  }

  /**
   * @return {string} the position of the timed metadata within the segment: 'S' (start), 'M' (mid) or 'E' (end)
   */;
  _proto.getTypeWithinSegment = function getTypeWithinSegment() {
    return this.typeWithinSegment;
  }

  /**
   * @param {TimedMetadata} previous the timed metadata under test
   * @return {boolean} true if the metadata under test is a duplicate. For this to be true, mediaId, segment
   * number, type within segment and offset must be equal.
   */;
  _proto.isDuplicate = function isDuplicate(previous) {
    return previous ? this.mediaId === previous.mediaId && this.segmentNumber === previous.segmentNumber && this.segmentCount === previous.segmentCount && this.typeWithinSegment === previous.typeWithinSegment && this.offset === previous.offset : false;
  };
  _proto.isFirstInSequence = function isFirstInSequence() {
    return this.isFirstInSegment() && this.segmentNumber === 1;
  };
  _proto.isFirstInSegment = function isFirstInSegment() {
    return this.typeWithinSegment === TypeWithinSegment.START;
  };
  _proto.isInSequence = function isInSequence(previous) {
    if (!previous) {
      return true;
    }
    if (this.mediaId !== previous.mediaId) {
      return this.isFirstInSequence();
    }
    var segmentDelta = this.segmentNumber - previous.segmentNumber;
    if (segmentDelta === 0) {
      return this.typeWithinSegment.isInSequence(previous.typeWithinSegment) || this.typeWithinSegment.isEqualMidType(previous.typeWithinSegment) && this.offset > previous.offset;
    }
    return segmentDelta > 0;
  };
  _proto.isLastInSegment = function isLastInSegment() {
    return this.typeWithinSegment === TypeWithinSegment.END;
  };
  _proto.isLastInSequence = function isLastInSequence() {
    return this.isLastInSegment() && this.isLastSegment();
  };
  _proto.isLastSegment = function isLastSegment() {
    return this.segmentNumber === this.segmentCount;
  };
  _proto.toString = function toString() {
    return "Metadata [ @" + this.playhead + " / " + this.mediaId + " / " + this.segmentNumber + ":" + this.segmentCount + " / " + this.typeWithinSegment.key + " : " + this.offset + " ]";
  };
  return TimedMetadata;
}();
var TypeWithinSegment = exports.TypeWithinSegment = /*#__PURE__*/function () {
  function TypeWithinSegment(key, order) {
    this.key = key;
    this.order = order;
  }
  TypeWithinSegment.getForKey = function getForKey(key) {
    if (!TypeWithinSegment.initialised) {
      var END = TypeWithinSegment.END;
      var MID = TypeWithinSegment.MID;
      var START = TypeWithinSegment.START;
      TypeWithinSegment.KEY_MAP.set(END.key, END);
      TypeWithinSegment.KEY_MAP.set(MID.key, MID);
      TypeWithinSegment.KEY_MAP.set(START.key, START);
      TypeWithinSegment.initialised = true;
    }
    return TypeWithinSegment.KEY_MAP.get(key);
  };
  var _proto2 = TypeWithinSegment.prototype;
  _proto2.isEqualMidType = function isEqualMidType(other) {
    if (!other) {
      return false;
    }
    return this.order === 1 && other.order === 1;
  };
  _proto2.isInSequence = function isInSequence(other) {
    if (!other) {
      return true;
    }
    return this.order > other.order;
  };
  return TypeWithinSegment;
}();
/** End type **/
TypeWithinSegment.END = new TypeWithinSegment("E", 2);
/** Mid type **/
TypeWithinSegment.MID = new TypeWithinSegment("M", 1);
/** Start type **/
TypeWithinSegment.START = new TypeWithinSegment("S", 0);
TypeWithinSegment.KEY_MAP = new Map();
TypeWithinSegment.initialised = false;